import { convertToDecimal } from "@/utils/convertToDecimal";
import {
  QueryKey,
  queryOptions,
  useSuspenseQuery,
} from "@tanstack/react-query";

export type CryptoCurrency = {
  circulating_supply: string;
  crypto_currency_historical_price_list: Array<{
    interval_type: "DAY" | "WEEK" | "MONTH" | "YEAR";
    max_irr_price: string;
    max_usdt_price: string;
    min_irr_price: string;
    min_usdt_price: string;
  }>;
  crypto_currency_name: string;
  daily_change_percentage: string;
  daily_change_percentage_in_irr: string;
  dominance: string;
  full_name: string;
  icon_path: string;
  infinite_supply: boolean;
  market_cap: string;
  max_supply: string | null;
  persian_name: string;
  price_in_irr: string;
  price_in_usdt: string;
  sparkline_7d_path: string;
  sparkline_historical_data: Array<{
    interval_type: "DAY" | "WEEK" | "MONTH" | "YEAR";
    sparkline_path: string;
  }>;
  total_supply: string;
  trading_value: string | null;
  volume_24h: string | null;
  weekly_change_percentage: string;
  weekly_change_percentage_in_irr: string;
  withdraw_status_type: "ACTIVE" | "INACTIVE";
  deposit_status_type: "ACTIVE" | "INACTIVE";
  trade_usdt_quote_status_type: "ACTIVE" | "INACTIVE";
  trade_irr_quote_status_type: "ACTIVE" | "INACTIVE";
  store_usdt_quote_status_type: "ACTIVE" | "INACTIVE";
  store_irr_quote_status_type: "ACTIVE" | "INACTIVE";
};

export type SearchCryptoCurrenciesStatsReturnType = {
  crypto_currencies: Array<CryptoCurrency>;
};

export async function searchCryptoCurrenciesStats(
  cryptoCurrencyTypes: string[],
): Promise<SearchCryptoCurrenciesStatsReturnType> {
  const params = new URLSearchParams();

  cryptoCurrencyTypes.forEach((cryptoCurrencyType) => {
    params.append("crypto_currency_types", cryptoCurrencyType);
  });

  const response = await fetch(
    `${process.env.APP_API_URL}/api/v2/market-data/crypto-currencies-stats?${params.toString()}`,
    { next: { revalidate: 60 * 2 } },
  );

  return (await response.json()) as SearchCryptoCurrenciesStatsReturnType;
}

const cryptoCurrenciesKeys = {
  stats: (cryptoCurrencyTypes: string[]): QueryKey => [
    "crypto-currencies",
    "stats",
    [...cryptoCurrencyTypes].sort(),
  ],
} as const;

export const cryptoCurrenciesStatsQueryOptions = (
  cryptoCurrencyTypes: string[],
) => {
  return queryOptions({
    queryKey: cryptoCurrenciesKeys.stats(cryptoCurrencyTypes),
    queryFn: () => searchCryptoCurrenciesStats(cryptoCurrencyTypes),
    enabled: cryptoCurrencyTypes.length !== 0,
    refetchInterval: 60 * 1000,
    select: (data): SearchCryptoCurrenciesStatsReturnType => {
      return {
        crypto_currencies: data.crypto_currencies.map(
          ({
            price_in_usdt,
            price_in_irr,
            weekly_change_percentage,
            weekly_change_percentage_in_irr,
            daily_change_percentage,
            daily_change_percentage_in_irr,
            circulating_supply,
            crypto_currency_historical_price_list,
            ...rest
          }) => {
            return {
              ...rest,
              price_in_usdt: convertToDecimal(price_in_usdt).toFixed(),
              price_in_irr: convertToDecimal(price_in_irr).toDP(0).toFixed(),
              weekly_change_percentage: convertToDecimal(
                weekly_change_percentage,
              )
                .toDP(2)
                .toFixed(),
              weekly_change_percentage_in_irr: convertToDecimal(
                weekly_change_percentage_in_irr,
              )
                .toDP(2)
                .toFixed(),
              daily_change_percentage: convertToDecimal(daily_change_percentage)
                .toDP(2)
                .toFixed(),
              daily_change_percentage_in_irr: convertToDecimal(
                daily_change_percentage_in_irr,
              )
                .toDP(2)
                .toFixed(),
              circulating_supply:
                convertToDecimal(circulating_supply).toFixed(),
              crypto_currency_historical_price_list:
                crypto_currency_historical_price_list.map(
                  ({
                    max_usdt_price,
                    min_usdt_price,
                    max_irr_price,
                    min_irr_price,
                    ...rest
                  }) => {
                    return {
                      ...rest,
                      max_usdt_price:
                        convertToDecimal(max_usdt_price).toFixed(),
                      min_usdt_price:
                        convertToDecimal(min_usdt_price).toFixed(),
                      max_irr_price: convertToDecimal(max_irr_price)
                        .toDP(0)
                        .toFixed(),
                      min_irr_price: convertToDecimal(min_irr_price)
                        .toDP(0)
                        .toFixed(),
                    };
                  },
                ),
            };
          },
        ),
      };
    },
  });
};

export const useCryptoCurrenciesStats = (cryptoCurrencyTypes: string[]) => {
  return useSuspenseQuery(
    cryptoCurrenciesStatsQueryOptions(cryptoCurrencyTypes),
  );
};
